// src/pages/OptimizationPage.tsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { currentOrigin, useBackTarget, withReturnTo } from '../modules/pricingJourney/navigation';
import HistoryIcon from '@mui/icons-material/History';
import {
  Typography,
  Box,
  TextField,
  Button,
  CircularProgress,
  Alert,
  AlertTitle,
  Autocomplete,
  Stack,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ButtonBase,
  SvgIcon,
} from '@mui/material';

// --- ICONOS UPDATE V2.0 (DARK PREMIUM) ---
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import { formatEuro, formatInteger, formatPercent } from '../modules/pricingJourney/formatNumber';
import {
  optimizePrice,
  getClientIds,
  getProductIds,
  getEntityNames,
  getProductAttributesFormSchema,
  getClientAttributesFormSchema,
  prepareOptimizationCache,
  getOptimizationCacheStatus,
  type OptimizationInput,
  type OptimizationOutput,
  type AttributeSchema,
  type ProductAttributesFormSchemaAPIResponse,
  type ClientAttributesFormSchemaAPIResponse,
  type OptimizationCacheStatusResponse,
} from '../services/optimizationService';

import { getRetrainingStatus } from '../services/diagnosisService';
import EnginePanel from '../modules/pricingJourney/components/EnginePanel';

import { useAuth } from '../context/AuthContext';
import ValueBridge from '../components/Optimization/ValueBridge'; 
import OptimizationLimitWidget from '../components/Optimization/OptimizationLimitWidget';
import PageSkeleton from '../modules/pricingJourney/components/Skeletons';
import EmptyStateHero from '../components/Common/EmptyStateHero';
import PageHeader from '../modules/pricingJourney/components/PageHeader';
import { pageFrameSx } from '../modules/pricingJourney/components/PageHeader';
import { SkeletonBlock } from '../modules/pricingJourney/components/Skeletons';
import { StatusBlock } from '../modules/pricingJourney/components/JourneyBlocks';
import { JourneyChip } from '../modules/pricingJourney/components/JourneyBasics';

// PRC-046 (boceto G6): estilos del panel, campos y bloques, iguales al resto de la app.
const panelSx = {
    bgcolor: 'rgba(15,23,42,.72)',
    border: '1px solid #1e293b',
    borderRadius: '16px',
};
// Campo como `.input` del boceto: 46 px, radio 10, fondo #0b1424.
const inputSx = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
        minHeight: 46,
        borderRadius: '10px',
        bgcolor: '#0b1424',
        color: '#f8fafc',
        fontSize: 15,
        '& fieldset': { borderColor: 'rgba(148,163,184,.28)' },
        '&:hover fieldset': { borderColor: 'rgba(148,163,184,.5)' },
        '&.Mui-focused fieldset': { borderColor: '#38bdf8', borderWidth: '1px' },
        '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(56,189,248,.12)' },
        '&.Mui-error fieldset': { borderColor: '#f87171' },
        '&.Mui-disabled': { bgcolor: '#0a1220' },
        '&.Mui-disabled fieldset': { borderColor: 'rgba(148,163,184,.18)' },
    },
    '& .MuiOutlinedInput-input': { py: '11.5px', px: '14px' },
    '& .MuiAutocomplete-inputRoot': { py: '4px', px: '6px' },
    '& .MuiOutlinedInput-input::placeholder, & .Mui-disabled .MuiOutlinedInput-input::placeholder': { color: '#64748b', opacity: 1 },
    '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root, & .MuiAutocomplete-popupIndicator': { color: '#94a3b8' },
};
const labelSx = { fontSize: 12, fontWeight: 700, color: '#cbd5e1' };
const hintSx = { fontSize: 12, color: '#94a3b8', lineHeight: 1.45 };

// Iconos del boceto G6 (mismos trazados).
const G6Icon: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <SvgIcon viewBox="0 0 24 24" sx={{ fontSize: 16, fill: 'none', stroke: 'currentColor', strokeWidth: 1.9, strokeLinecap: 'round', strokeLinejoin: 'round' }}>
        {children}
    </SvgIcon>
);
const ClientIcon = () => <G6Icon><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></G6Icon>;
const ProductIcon = () => <G6Icon><path d="m12 3 9 5-9 5-9-5z" /><path d="m3 13 9 5 9-5" /></G6Icon>;
const StrategyIcon = () => <G6Icon><path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M6 18l2.5-2.5M15.5 8.5 18 6" /></G6Icon>;

// Título pequeño con icono y, a la derecha, su botón «+ … nuevo».
const SectionHead: React.FC<{ icon: React.ReactNode; title: string; action?: React.ReactNode }> = ({ icon, title, action }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <Box component="h3" sx={{ m: 0, fontSize: 14, fontWeight: 700, color: '#f8fafc', display: 'flex', gap: '8px', alignItems: 'center', '& svg': { color: '#94a3b8' } }}>
            {icon}{title}
        </Box>
        {action}
    </Box>
);

const NewToggle: React.FC<{ on: boolean; label: string; onClick: () => void; disabled?: boolean }> = ({ on, label, onClick, disabled }) => (
    <ButtonBase
        onClick={onClick}
        disabled={disabled}
        aria-pressed={on}
        sx={{
            height: 28, px: '10px', borderRadius: '8px', gap: '6px', fontFamily: 'inherit', fontSize: 12, fontWeight: 700,
            border: '1px solid', borderColor: on ? '#38bdf8' : '#334155',
            bgcolor: on ? '#38bdf8' : 'transparent', color: on ? '#0f172a' : '#94a3b8',
            '&:hover': on ? { bgcolor: '#7dd3fc', borderColor: '#7dd3fc' } : { borderColor: '#38bdf8', color: '#7dd3fc' },
            '&.Mui-disabled': { opacity: 0.6 },
        }}
    >
        {on ? `✓ ${label}` : `+ ${label}`}
    </ButtonBase>
);

// Recuadro discontinuo «Datos del … nuevo».
const NewEntityBox: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <Box sx={{ border: '1px dashed #334155', borderRadius: '10px', p: '12px', display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px', bgcolor: 'rgba(0,0,0,.2)' }}>
        <Box component="span" sx={{ gridColumn: '1 / -1', fontSize: 11, fontWeight: 800, letterSpacing: '.12em', textTransform: 'uppercase', color: '#fcd34d' }}>{title}</Box>
        {children}
        <Box component="span" sx={{ gridColumn: '1 / -1', fontSize: 11, color: '#94a3b8' }}>Campos según los atributos configurados en tu cuenta.</Box>
    </Box>
);

// PRC-046 (G6): nombre junto al código, para toda la lista (los mismos nombres que usa Margin Center).
const useEntityNames = (kind: 'customer' | 'product'): Map<string, string> => {
    const { data } = useQuery({
        queryKey: ['quick-evaluation', 'entity-names', kind],
        queryFn: () => getEntityNames(kind),
        staleTime: 5 * 60 * 1000,
        retry: 1,
        refetchOnWindowFocus: false,
    });
    return useMemo(() => new Map(Object.entries(data ?? {})), [data]);
};
const entityLabel = (id: string | null | undefined, names: Map<string, string>) => {
    if (id == null) return '';
    const name = names.get(String(id));
    return name ? `${id} · ${name}` : String(id);
};

// --- ESTADOS PARA LA MÁQUINA DE ESTADOS DE UI ---
type UIState =
  | { name: 'idle' }
  | { name: 'optimizing' }
  | { name: 'success'; finalData: OptimizationOutput }
  | { name: 'error'; error: Error };

const STRATEGY_LABEL: Record<string, string> = { conservador: 'Conservadora', equilibrado: 'Equilibrada', agresivo: 'Agresiva', manual: 'manual' };

const OptimizationPage: React.FC = () => {
  const { user, refreshProfile } = useAuth(); 
  const navigate = useNavigate();
  // PRC-027: «Volver» al centro de Optimización (o a la guía si viene de ella).
  const back = useBackTarget('/optimization-center', 'Volver a Optimización');
  const backButton = (
    <Button
      size="small"
      startIcon={<ArrowBackRoundedIcon />}
      onClick={back.go}
      sx={{ alignSelf: 'flex-start', minWidth: 0, px: 0, mb: 2, color: '#7dd3fc', textTransform: 'none', fontWeight: 850, '&:hover': { bgcolor: 'transparent', color: '#bae6fd' } }}
    >
      {back.label}
    </Button>
  );
  const [uiState, setUiState] = useState<UIState>({ name: 'idle' });
  const [strategySensitivity, setStrategySensitivity] = useState<number>(50);

  // PRC-046: el panel es fijo con su propio scroll; el pie con el botón siempre a la vista.
  // Su altura máxima es lo que queda de pantalla desde donde empieza (al cargar, bajo la
  // cabecera; al hacer scroll, a 20 px del borde), para que el botón no quede fuera.
  const panelRef = useRef<HTMLElement | null>(null);
  const [panelMaxHeight, setPanelMaxHeight] = useState(800);
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const el = panelRef.current;
      if (!el) return;
      const top = Math.max(el.getBoundingClientRect().top, 20);
      setPanelMaxHeight(Math.max(320, Math.floor(window.innerHeight - top - 20)));
    };
    const schedule = () => { if (!frame) frame = window.requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (frame) window.cancelAnimationFrame(frame);
    };
  });

  // 1. CONSULTA DE ESTADO DEL MOTOR (OMEGA CORE)
  const { data: retrainingStatus, isLoading: isStatusLoading } = useQuery({
    queryKey: ['retraining-status'],
    queryFn: getRetrainingStatus,
    refetchInterval: (query) => (['running', 'pending', 'ready_to_train'].includes(query.state.data?.status ?? '') ? 30000 : 60000)
  });

  // 2. DERIVACIÓN DEL ESTADO DEL SISTEMA
  const systemState = useMemo(() => {
    if (isStatusLoading) return 'LOADING';
    if (!retrainingStatus || retrainingStatus.status === 'not_found') return 'NO_DATA';
    if (retrainingStatus.status === 'ready_to_train') return 'NEEDS_TRAINING';
    // PRC-041: sin un modelo listo de antes, el primer entrenamiento en curso o fallido no es «activo».
    if (!retrainingStatus.has_ready_model) {
      if (retrainingStatus.status === 'running' || retrainingStatus.status === 'pending') return 'NEEDS_TRAINING';
      // Cualquier otro estado sin modelo listo (error o desconocido) es un fallo.
      if (retrainingStatus.status !== 'success' && retrainingStatus.status !== 'success_with_warnings') return 'ENGINE_FAILED';
    }
    // Optimización individual SÍ puede funcionar en AUDIT_PENDING porque el modelo ya está entrenado
    return 'ACTIVE';
  }, [retrainingStatus, isStatusLoading]);

  // --- READINESS DEL SERVING DE PRICING ---
  const cachePreparationRequestedForUserRef = useRef<string | null>(null);
  const [cachePreparationTriggerError, setCachePreparationTriggerError] =
    useState<string | null>(null);
  const [cacheReadinessTimedOut, setCacheReadinessTimedOut] =
    useState(false);

  const {
    data: optimizationCacheStatus,
    isError: isOptimizationCacheStatusError,
    error: optimizationCacheStatusError,
    refetch: refetchOptimizationCacheStatus,
  } = useQuery<OptimizationCacheStatusResponse, Error>({
    queryKey: ['optimization-cache-status', user?.id],
    queryFn: getOptimizationCacheStatus,
    enabled: Boolean(user?.id) && systemState === 'ACTIVE',
    retry: 1,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      if (cacheReadinessTimedOut) {
        return false;
      }

      const currentStatus = query.state.data?.status;

      if (
        currentStatus === 'ready' ||
        currentStatus === 'failed'
      ) {
        return false;
      }

      return 3000;
    },
  });

  const isOptimizationCacheReady =
    optimizationCacheStatus?.status === 'ready';

  const optimizationCacheFailureMessage =
    cachePreparationTriggerError ||
    optimizationCacheStatus?.message ||
    optimizationCacheStatusError?.message ||
    null;

  const isOptimizationCacheFailed =
    optimizationCacheStatus?.status === 'failed' ||
    isOptimizationCacheStatusError ||
    cachePreparationTriggerError !== null;

  const isOptimizationCachePreparing =
    systemState === 'ACTIVE' &&
    !isOptimizationCacheReady &&
    !isOptimizationCacheFailed &&
    !cacheReadinessTimedOut;

  // Reiniciamos exclusivamente el estado local cuando cambia el usuario.
  useEffect(() => {
    cachePreparationRequestedForUserRef.current = null;
    setCachePreparationTriggerError(null);
    setCacheReadinessTimedOut(false);
  }, [user?.id]);

  // Solicita la preparación únicamente si Redis indica que no existe
  // o que una preparación anterior terminó explícitamente en fallo.
  useEffect(() => {
    if (!user?.id || systemState !== 'ACTIVE') {
      return;
    }

    const currentStatus = optimizationCacheStatus?.status;

    if (
      currentStatus !== 'not_found' &&
      currentStatus !== 'failed'
    ) {
      return;
    }

    if (
      cachePreparationRequestedForUserRef.current === user.id
    ) {
      return;
    }

    cachePreparationRequestedForUserRef.current = user.id;
    setCachePreparationTriggerError(null);

    let cancelled = false;

    const requestCachePreparation = async () => {
      const preparationResponse =
        await prepareOptimizationCache();

      if (cancelled) {
        return;
      }

      if (
        preparationResponse.status ===
        'cache_preparation_failed'
      ) {
        setCachePreparationTriggerError(
          'No se pudo iniciar la preparación del motor de precios.',
        );
        return;
      }

      // Actualiza inmediatamente el estado después del POST.
      await refetchOptimizationCacheStatus();
    };

    void requestCachePreparation();

    return () => {
      cancelled = true;
    };
  }, [
    user?.id,
    systemState,
    optimizationCacheStatus?.status,
    refetchOptimizationCacheStatus,
  ]);

  // El sondeo es acotado. Nunca dejamos la pantalla consultando
  // indefinidamente un worker que podría haber desaparecido.
  useEffect(() => {
    if (
      systemState !== 'ACTIVE' ||
      isOptimizationCacheReady ||
      isOptimizationCacheFailed
    ) {
      setCacheReadinessTimedOut(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setCacheReadinessTimedOut(true);
    }, 180000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    user?.id,
    systemState,
    isOptimizationCacheReady,
    isOptimizationCacheFailed,
  ]);

  // Estados para selección principal
  const [clienteId, setClienteId] = useState<string | null>(null);
  const [productoId, setProductoId] = useState<string | null>(null);
  const [costeActual, setCosteActual] = useState('');
  const [cantidadInput, setCantidadInput] = useState<string>('');

  // Estados para controlar UI de "Nuevo Cliente" y "Nuevo Producto"
  const [isNewClientUI, setIsNewClientUI] = useState<boolean>(false);
  const [isNewProductUI, setIsNewProductUI] = useState<boolean>(false);

  // Estados para atributos dinámicos
  const [dynamicClientAttrs, setDynamicClientAttrs] = useState<Record<string, string | null>>({});
  const [dynamicProductAttrs, setDynamicProductAttrs] = useState<Record<string, string | null>>({});

  const [selectedStrategy, setSelectedStrategy] = useState<string>('equilibrado');

  // --- React Query para cargar opciones de Clientes Existentes ---
  const {
    data: clientOptions = [],
    isLoading: loadingClients,
    isError: isClientError,
    error: clientError
  } = useQuery<string[], Error>({
    queryKey: ['clientIds'],
    queryFn: getClientIds,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  // --- React Query para cargar opciones de Productos Existentes ---
  const {
    data: productOptions = [],
    isLoading: loadingProducts,
    isError: isProductError,
    error: productError
  } = useQuery<string[], Error>({
    queryKey: ['productIds'],
    queryFn: getProductIds,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  // PRC-046: «C-1042 · Nombre» en los selectores.
  const clientNames = useEntityNames('customer');
  const productNames = useEntityNames('product');

  // --- React Query para cargar el esquema de atributos de NUEVO CLIENTE ---
  const {
    data: clientAttrSchemaResponse,
    isLoading: loadingClientAttrSchema,
    // isError: isClientAttrSchemaError,
    error: clientAttrSchemaError,
  } = useQuery<ClientAttributesFormSchemaAPIResponse, Error>({
    queryKey: ['clientAttributesFormSchema'],
    queryFn: getClientAttributesFormSchema,
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    enabled: isNewClientUI, // Habilitar solo si isNewClientUI es true
    refetchOnWindowFocus: false,
  });

  const currentClientAttrFeatures: AttributeSchema[] =
    clientAttrSchemaResponse?.success && clientAttrSchemaResponse.data?.new_client_features
      ? clientAttrSchemaResponse.data.new_client_features
      : [];

  // --- React Query para cargar el esquema de atributos de NUEVO PRODUCTO ---
  const {
    data: productAttrSchemaResponse,
    isLoading: loadingProductAttrSchema,
    // isError: isProductAttrSchemaError,
    error: productAttrSchemaError,
  } = useQuery<ProductAttributesFormSchemaAPIResponse, Error>({
    queryKey: ['productAttributesFormSchema'],
    queryFn: getProductAttributesFormSchema,
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    enabled: isNewProductUI, // Habilitar solo si isNewProductUI es true
    refetchOnWindowFocus: false,
  });

  const currentProductAttrFeatures: AttributeSchema[] =
    productAttrSchemaResponse?.success && productAttrSchemaResponse.data?.new_product_features
      ? productAttrSchemaResponse.data.new_product_features
      : [];

  // --- Estados derivados para UI de carga/error de opciones ---
  const loadingInitialOptions = loadingClients || loadingProducts;
  const initialOptionsError = isClientError || isProductError ? "Error al cargar listas de clientes o productos." : null;

  const optimizationMutation = useMutation<OptimizationOutput, Error, { payload: OptimizationInput }>({
  mutationFn: (vars) => optimizePrice(vars.payload, false),
  onSuccess: (data) => {
    setUiState({ name: 'success', finalData: data });

    // --- NUEVO: ACTUALIZAR CONTADOR EN TIEMPO REAL ---
    // Esperamos 1s para asegurar que el Backend terminó de sumar +1
    setTimeout(() => {
        refreshProfile().catch(console.error);
    }, 1000);
  },
  onError: (error) => {
    setUiState({ name: 'error', error });
  }
});

  // Control de Límite de Tiers
  const [isLimitReached, setIsLimitReached] = useState(false);



  // --- useEffects para limpiar campos cuando se cambia el estado de "Nuevo" ---
  useEffect(() => {
    if (isNewClientUI) {
      setClienteId(null); // Limpiar cliente existente seleccionado
      setDynamicClientAttrs({}); // Limpiar atributos dinámicos de cliente
    } else {
      setDynamicClientAttrs({});
    }
  }, [isNewClientUI]);

  useEffect(() => {
    if (isNewProductUI) {
      setProductoId(null); // Limpiar producto existente seleccionado
      setDynamicProductAttrs({}); // Limpiar atributos dinámicos de producto
    } else {
      setDynamicProductAttrs({});
      // Considerar si el coste también debe limpiarse si no es producto nuevo
      // setCosteActual(''); // Opcional: si el coste solo tiene sentido para producto nuevo
    }
  }, [isNewProductUI]);

  // --- Handlers para cambios en los campos dinámicos ---
  const handleDynamicClientAttrChange = (name: string, value: string | null) => {
    setDynamicClientAttrs(prev => ({ ...prev, [name]: value }));
  };

  const handleDynamicProductAttrChange = (name: string, value: string | null) => {
    setDynamicProductAttrs(prev => ({ ...prev, [name]: value }));
  };

  const handleStrategyChange = (
    _event: React.MouseEvent<HTMLElement>,
    newStrategy: string | null,
  ) => {
    if (newStrategy !== null) {
      setSelectedStrategy(newStrategy);
      // Mapeo Preset -> Slider Math
      if (newStrategy === 'conservador') setStrategySensitivity(25);
      if (newStrategy === 'equilibrado') setStrategySensitivity(50);
      if (newStrategy === 'agresivo') setStrategySensitivity(90);
    }
  };

  const handleSliderChange = (_event: Event, newValue: number | number[]) => {
    setStrategySensitivity(newValue as number);
    setSelectedStrategy(''); // Deselecciona botones para indicar "Modo Manual"
  };

  const [formAttempted, setFormAttempted] = useState<boolean>(false);



  const handleOptimize = (event: React.FormEvent) => {
    event.preventDefault();

    if (!isOptimizationCacheReady) {
      alert(
        'El motor de precios todavía se está preparando. La optimización estará disponible cuando finalice.',
      );
      return;
    }

    setFormAttempted(true);
    setUiState({ name: 'optimizing' });

    if (!user?.id) {
      alert("Error de autenticación. Por favor, inicie sesión de nuevo.");
      return;
    }

    // Validaciones
    if (!isNewClientUI && !clienteId) {
      alert("Debe seleccionar un ID de Cliente existente o marcar 'Nuevo Cliente'.");
      return;
    }
    if (isNewClientUI && currentClientAttrFeatures.some(attr => attr.required && !dynamicClientAttrs[attr.name])) {
      // Esta validación es básica. Idealmente, se especificaría mejor qué atributos de cliente nuevo son obligatorios.
      alert("Debe completar todos los campos requeridos para el nuevo cliente.");
      return;
    }
    if (!isNewProductUI && !productoId) {
      alert("Debe seleccionar un ID de Producto existente o marcar 'Nuevo Producto'.");
      return;
    }
    if (isNewProductUI) {
      const costNum = parseFloat(costeActual);
      if (isNaN(costNum) || costNum <= 0) {
        alert("Para un producto nuevo, el 'Coste del Producto' es obligatorio y debe ser un número positivo.");
        return;
      }
      if (currentProductAttrFeatures.some(attr => attr.required && !dynamicProductAttrs[attr.name])) {
        // Esta validación es básica. Idealmente, se especificaría mejor qué atributos de producto nuevo son obligatorios.
        alert("Debe completar todos los campos requeridos para el nuevo producto.");
        return;
      }
    }


    // --- CONSTRUCCIÓN DE PAYLOAD ROBUSTA Y SEGURA ---
    const newProductAttributesPayload: OptimizationInput['new_product_attributes'] = isNewProductUI
      ? {
          standard_cost: parseFloat(costeActual), // Ya validado
          ...currentProductAttrFeatures.reduce((acc, attr) => {
            const value = dynamicProductAttrs[attr.name];
            if (value !== undefined && value !== null && value !== '') {
              acc[attr.name] = value;
            }
            return acc;
          }, {} as Record<string, any>)
        }
      : null;

    const parsedQty = cantidadInput && !isNaN(parseFloat(cantidadInput)) && parseFloat(cantidadInput) > 0 
        ? parseFloat(cantidadInput) 
        : null;

    const payload: OptimizationInput = {
      client_id: isNewClientUI ? "NEW_CLIENT_PLACEHOLDER" : clienteId!, is_new_client: isNewClientUI, new_client_attributes: isNewClientUI ? dynamicClientAttrs : null,
      product_id: isNewProductUI ? null : productoId, is_new_product: isNewProductUI, new_product_attributes: newProductAttributesPayload,
      strategy_profile: selectedStrategy || 'manual', 
      strategy_sensitivity: strategySensitivity,      
      coste_actual_existente: !isNewProductUI && costeActual ? parseFloat(costeActual) : null,
      generate_pricing_matrix: true, 
      quantity: parsedQty // Enviar null si está vacío
    };
    // PRC-038: el resultado describe lo calculado, no lo que se cambie después en el formulario.
    lastRunRef.current = { strategy: payload.strategy_profile || 'manual', sensitivity: strategySensitivity, quantity: parsedQty };
    optimizationMutation.mutate({ payload }); // Invocación corregida
  };

// --- OBTENER DATOS FINALES PARA RENDERIZAR ---
  const getFinalData = (): OptimizationOutput | null => {
    if (uiState.name === 'success') return uiState.finalData;
    return null;
  };
  const finalData = getFinalData();
  const lastRunRef = useRef<{ strategy: string; sensitivity: number; quantity: number | null } | null>(null);

  const getOptimizeErrorMessage = (error: Error | null): string | null => {
    if (!error) return null;
    let errorMessage = 'Error desconocido al realizar la optimización.';
    const axiosError = error as any;
    if (axiosError.response) {
        const status = axiosError.response.status;
        const detail = axiosError.response.data?.detail;
        if (status === 401) return "Error de autenticación. Por favor, vuelva a iniciar sesión.";
        if (status === 404 && detail?.includes("Cliente") && detail?.includes("no encontrado")) return "Cliente no encontrado con los datos base.";
        if (status === 404 && detail?.includes("Producto") && detail?.includes("no encontrado")) return "Producto no encontrado con los datos base.";
        if (status === 404) return "Recurso no encontrado. Verifique los IDs o atributos proporcionados.";
        if (status === 422 && detail) {
            if (typeof detail === 'string') { return detail; }
            if (Array.isArray(detail)) { return detail.map((e: any) => `${e.loc?.slice(-1)[0] || 'campo'}: ${e.msg}`).join('; '); }
        }
        if(typeof detail === 'string' && detail) return detail;
        errorMessage = `Error del servidor (${status})`;
    } else if (axiosError.request) {
        errorMessage = 'No se pudo conectar con el servidor. Verifique su conexión.';
    } else if (error.message) {
        errorMessage = error.message;
    }
    console.error("Error formateado para UI:", errorMessage, "Error original:", error);
    return errorMessage;
  };


  // --- Lógica para deshabilitar el botón Optimizar ---
  const isCostInvalidForNewProduct = isNewProductUI && (costeActual === '' || isNaN(parseFloat(costeActual)) || parseFloat(costeActual) <= 0);
  const isOptimizeButtonDisabled =
  !isOptimizationCacheReady ||
  loadingInitialOptions ||
  (isNewClientUI && loadingClientAttrSchema) ||
  (isNewProductUI && loadingProductAttrSchema) ||
  uiState.name === 'optimizing' || // <-- MODIFICADO
  (!isNewClientUI && !clienteId) ||
  (!isNewProductUI && !productoId) ||
  isCostInvalidForNewProduct ||
  isLimitReached;

  // Helper para renderizar campos de atributos dinámicos.
  // PRC-046 (G6): dos columnas dentro del recuadro discontinuo, etiqueta encima.
  const renderDynamicAttributeFields = (
    attributes: AttributeSchema[],
    values: Record<string, string | null>,
    handler: (name: string, value: string | null) => void,
    isLoadingSchema: boolean,
    schemaError: Error | null,
    schemaErrorResponse?: any, // Para mensajes de error específicos de la API
    entityName: string = "entidad"
  ) => {
    if (isLoadingSchema) {
      return (
        <Box sx={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 1, ...hintSx }}>
          <CircularProgress size={16} />Cargando los datos del {entityName} nuevo…
        </Box>
      );
    }
    if (schemaError) {
      return (
        <Alert severity="warning" sx={{ gridColumn: '1 / -1' }}>
          No se pudieron cargar los datos del {entityName} nuevo: {schemaError?.message || 'error desconocido.'}
          {schemaErrorResponse && !schemaErrorResponse.success && schemaErrorResponse.message &&
            <Typography variant="caption" display="block">{schemaErrorResponse.message}</Typography>
          }
        </Alert>
      );
    }
    if (attributes.length === 0) {
      return <Box component="span" sx={{ gridColumn: '1 / -1', ...hintSx }}>No hay datos adicionales configurados para un {entityName} nuevo.</Box>;
    }
    return attributes.map((attr) => {
      const fieldId = `dynamic-${entityName}-attr-${attr.name}`;
      const label = `${attr.label || attr.name}${attr.required ? ' *' : ''}`;
      return (
        <Box key={fieldId} sx={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }}>
          <Box component="label" htmlFor={fieldId} sx={labelSx}>{label}</Box>
          {attr.type === 'select' && (
            <Autocomplete
              fullWidth
              id={fieldId}
              options={attr.options || []}
              value={values[attr.name] || null}
              onChange={(_event: any, newValue: string | null) => { handler(attr.name, newValue); }}
              getOptionLabel={(option) => String(option) || ""}
              isOptionEqualToValue={(option, value) => String(option) === String(value)}
              renderInput={(params) => <TextField {...params} required={attr.required || false} sx={inputSx} />}
              disabled={loadingInitialOptions || optimizationMutation.isPending}
            />
          )}
          {(attr.type === 'text' || attr.type === 'number') && (
            <TextField
              fullWidth
              id={fieldId}
              type={attr.type}
              value={values[attr.name] || ''}
              onChange={(e) => handler(attr.name, e.target.value)}
              required={attr.required || false}
              disabled={loadingInitialOptions || optimizationMutation.isPending}
              sx={inputSx}
            />
          )}
        </Box>
      );
    });
  };

  // --- GESTIÓN DE RENDERIZADO POR ESTADO OMEGA ---

  // CASO 0: CARGA DE SEGURIDAD
  if (systemState === 'LOADING') {
    // PRC-037: esqueleto de la página en lugar de la pantalla completa «PROAPricing OS».
    return <PageSkeleton eyebrow="Optimización" title="Evaluación rápida" variant="detail" />;
  }

  // CASO 1: SIN DATOS (Manda a cargar)
  if (systemState === 'NO_DATA') {
    return (
        <Box sx={{ minHeight: '80vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            {backButton}
            <EmptyStateHero 
                title="Aún no hay datos para evaluar precios"
                description="Para recomendar precios, PROA necesita tu histórico de ventas y tus costes."
            />
            {/* PRC-028: salida propia hacia la carga, con origen. */}
            <Button
                variant="contained"
                onClick={() => navigate(withReturnTo('/data-upload', currentOrigin(window.location)))}
                sx={{ mt: 3, bgcolor: '#38bdf8', color: '#0f172a', fontWeight: 'bold', textTransform: 'none' }}
            >
                Cargar datos
            </Button>
        </Box>
    );
  }

  // PRC-038/041: sin pantalla «Iniciar entrenamiento del motor»: se muestra «Preparando el motor» en la columna derecha.

  return (
    <Box sx={pageFrameSx}>
        {/* PRC-032/033: cabecera común. */}
        <PageHeader
            eyebrow="Optimización"
            title="Evaluación rápida"
            description="Elige cliente, producto y estrategia, y calcula el precio que recomienda PROA."
            back={{ label: back.label, onClick: back.go }}
            actions={(
                <Button
                    variant="outlined"
                    startIcon={<HistoryIcon />}
                    onClick={() => navigate(withReturnTo('/optimization/history', currentOrigin(window.location)))}
                    sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 800 }}
                >
                    Ver historial
                </Button>
            )}
        />

        {/* PRC-046 (boceto G6): un solo panel de 440 px a la izquierda, fijo al hacer scroll
            del resultado; si es más alto que la pantalla, su contenido se desplaza y el pie
            con «Calcular precio recomendado» queda siempre a la vista. */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '440px minmax(0, 1fr)' }, gap: '20px', alignItems: 'start' }}>
        <Box
          component="section"
          aria-label="Datos de la evaluación"
          sx={{
            ...panelSx,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            position: { lg: 'sticky' },
            top: { lg: 20 },
            maxHeight: { lg: `${panelMaxHeight}px` },
          }}
          ref={panelRef}
        >
          <Box
            component="form"
            id="quick-evaluation-form"
            onSubmit={handleOptimize}
            noValidate
            sx={{ p: '20px 24px', display: 'flex', flexDirection: 'column', gap: '18px', overflowY: { lg: 'auto' }, minHeight: 0, flex: '1 1 auto' }}
          >
            {/* Uso de la prueba (x de 1.000 optimizaciones). */}
            <OptimizationLimitWidget onLimitReached={setIsLimitReached} />

            {isOptimizationCachePreparing && (
              <Alert severity="info" icon={<CircularProgress size={20} />}>
                <AlertTitle>Preparando motor de precios</AlertTitle>
                Estamos cargando de forma segura los modelos necesarios.
                El formulario puede completarse, pero la optimización se
                habilitará únicamente cuando el motor esté listo.
              </Alert>
            )}

            {(isOptimizationCacheFailed || cacheReadinessTimedOut) && (
              <Alert severity="error">
                <AlertTitle>El motor de precios no está disponible</AlertTitle>
                {cacheReadinessTimedOut
                  ? 'La preparación no finalizó dentro de los tres minutos permitidos. No se ha enviado ninguna optimización ni se ha consumido cuota.'
                  : optimizationCacheFailureMessage ||
                    'La preparación del motor terminó con un error. No se ha enviado ninguna optimización.'}
              </Alert>
            )}

            {initialOptionsError && !loadingInitialOptions && (
              <Alert severity="warning">
                {initialOptionsError}
                {isClientError && <Typography variant="caption" display="block">Error clientes: {clientError?.message}</Typography>}
                {isProductError && <Typography variant="caption" display="block">Error productos: {productError?.message}</Typography>}
              </Alert>
            )}

            {/* Cliente */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <SectionHead
                icon={<ClientIcon />}
                title="Cliente"
                action={<NewToggle on={isNewClientUI} label="Cliente nuevo" onClick={() => setIsNewClientUI(prev => !prev)} disabled={loadingInitialOptions || optimizationMutation.isPending} />}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <Box component="label" htmlFor="client_id_autocomplete" sx={labelSx}>{isNewClientUI ? 'Cliente' : 'Cliente *'}</Box>
                <Autocomplete
                  id="client_id_autocomplete"
                  fullWidth
                  options={clientOptions}
                  value={isNewClientUI ? null : clienteId}
                  onChange={(_event: any, newValue: string | null) => { setClienteId(newValue); }}
                  getOptionLabel={(option) => entityLabel(option, clientNames)}
                  isOptionEqualToValue={(option, value) => String(option) === String(value)}
                  loading={loadingClients}
                  loadingText="Cargando clientes…"
                  noOptionsText="Sin coincidencias"
                  disabled={loadingInitialOptions || optimizationMutation.isPending || isNewClientUI}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={isNewClientUI ? 'Desactivado en modo cliente nuevo' : 'Busca por código o nombre'}
                      error={isClientError || (formAttempted && !isNewClientUI && !clienteId)}
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingClients ? <CircularProgress color="inherit" size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
              </Box>
              {isNewClientUI && (
                <NewEntityBox title="Datos del cliente nuevo">
                  {renderDynamicAttributeFields(
                    currentClientAttrFeatures,
                    dynamicClientAttrs,
                    handleDynamicClientAttrChange,
                    loadingClientAttrSchema,
                    clientAttrSchemaError,
                    clientAttrSchemaResponse,
                    "cliente"
                  )}
                </NewEntityBox>
              )}
            </Box>

            {/* Producto */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <SectionHead
                icon={<ProductIcon />}
                title="Producto"
                action={<NewToggle on={isNewProductUI} label="Producto nuevo" onClick={() => setIsNewProductUI(prev => !prev)} disabled={loadingInitialOptions || optimizationMutation.isPending} />}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <Box component="label" htmlFor="producto_id_autocomplete" sx={labelSx}>{isNewProductUI ? 'Producto' : 'Producto *'}</Box>
                <Autocomplete
                  id="producto_id_autocomplete"
                  fullWidth
                  options={productOptions}
                  value={isNewProductUI ? null : productoId}
                  onChange={(_event: any, newValue: string | null) => { setProductoId(newValue); }}
                  getOptionLabel={(option) => entityLabel(option, productNames)}
                  isOptionEqualToValue={(option, value) => String(option) === String(value)}
                  loading={loadingProducts}
                  loadingText="Cargando productos…"
                  noOptionsText="Sin coincidencias"
                  disabled={loadingInitialOptions || optimizationMutation.isPending || isNewProductUI}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={isNewProductUI ? 'Desactivado en modo producto nuevo' : 'Busca por código o nombre'}
                      error={isProductError}
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingProducts ? <CircularProgress color="inherit" size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
              </Box>
              {isNewProductUI && (
                <NewEntityBox title="Datos del producto nuevo">
                  {renderDynamicAttributeFields(
                    currentProductAttrFeatures,
                    dynamicProductAttrs,
                    handleDynamicProductAttrChange,
                    loadingProductAttrSchema,
                    productAttrSchemaError,
                    productAttrSchemaResponse,
                    "producto"
                  )}
                </NewEntityBox>
              )}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }}>
                  <Box component="label" htmlFor="coste_actual" sx={labelSx}>{isNewProductUI ? 'Coste estándar (€) *' : 'Coste estándar (€)'}</Box>
                  <TextField
                    fullWidth
                    id="coste_actual"
                    name="coste_actual"
                    type="number"
                    inputProps={{ step: "0.01", min: "0.01", inputMode: 'decimal' }}
                    value={costeActual}
                    onChange={(e) => setCosteActual(e.target.value)}
                    disabled={loadingInitialOptions || optimizationMutation.isPending}
                    required={isNewProductUI}
                    error={isNewProductUI && (costeActual === '' || isNaN(parseFloat(costeActual)) || parseFloat(costeActual) <= 0)}
                    sx={inputSx}
                  />
                  <Box component="span" sx={hintSx}>Obligatorio en producto nuevo; opcional si existe</Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }}>
                  <Box component="label" htmlFor="cantidad_input" sx={labelSx}>Cantidad</Box>
                  <TextField
                    fullWidth
                    id="cantidad_input"
                    type="number"
                    value={cantidadInput}
                    onChange={(e) => setCantidadInput(e.target.value)}
                    disabled={loadingInitialOptions || optimizationMutation.isPending}
                    inputProps={{ min: 1, inputMode: 'numeric' }}
                    sx={inputSx}
                  />
                  <Box component="span" sx={hintSx}>Opcional · por defecto 1 unidad</Box>
                </Box>
              </Box>
            </Box>

            {/* Estrategia */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <SectionHead icon={<StrategyIcon />} title="Estrategia" />
              <Box role="radiogroup" aria-label="Estrategia" sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px' }}>
                {[
                  { id: 'conservador', label: 'Conservadora', desc: 'Prioriza conservar el volumen y el cliente.' },
                  { id: 'equilibrado', label: 'Equilibrada', desc: 'Equilibra margen y riesgo de perder ventas.' },
                  { id: 'agresivo', label: 'Agresiva', desc: 'Busca el máximo margen por unidad.' },
                ].map((strategy) => {
                  const on = selectedStrategy === strategy.id;
                  return (
                    <ButtonBase
                      key={strategy.id}
                      role="radio"
                      aria-checked={on}
                      onClick={(e) => handleStrategyChange(e as any, strategy.id)}
                      sx={{
                        display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start', gap: '4px',
                        p: '10px', borderRadius: '10px', textAlign: 'left', fontFamily: 'inherit',
                        border: '1px solid', borderColor: on ? '#38bdf8' : '#233149',
                        bgcolor: on ? 'rgba(56,189,248,.08)' : 'transparent',
                        '&:hover': { borderColor: '#38bdf8' },
                      }}
                    >
                      <Box component="b" sx={{ fontSize: 13, color: '#f8fafc' }}>{strategy.label}</Box>
                      <Box component="span" sx={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.4 }}>{strategy.desc}</Box>
                    </ButtonBase>
                  );
                })}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: '6px' }}>
                <Box component="span" id="strategy-sensitivity-label" sx={{ fontSize: 12, fontWeight: 700, color: '#cbd5e1' }}>Cuánto seguir la recomendación del modelo</Box>
                <Box component="b" sx={{ fontSize: 12, color: '#38bdf8' }}>{strategySensitivity} %</Box>
              </Box>
              <Slider
                value={strategySensitivity}
                onChange={handleSliderChange}
                step={1}
                min={0}
                max={100}
                aria-labelledby="strategy-sensitivity-label"
                sx={{
                  color: '#38bdf8',
                  height: 6,
                  py: '10px',
                  '& .MuiSlider-rail': { bgcolor: '#1e293b', opacity: 1 },
                  '& .MuiSlider-track': { border: 'none' },
                  '& .MuiSlider-thumb': {
                    height: 20, width: 20, backgroundColor: '#1e293b', border: '2px solid #38bdf8',
                    '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': { boxShadow: '0 0 0 6px rgba(56,189,248,.16)' },
                    '&:before': { display: 'none' },
                  },
                }}
              />
              <Box component="span" sx={{ fontSize: 12, color: '#94a3b8', mt: '-4px' }}>
                Con 0 % el precio se queda como lo pondrías hoy; con 100 % se aplica toda la mejora de margen que ve el modelo.
              </Box>
            </Box>
          </Box>

          {/* Pie del panel: siempre a la vista. */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', p: '14px 24px', borderTop: '1px solid #1e293b', bgcolor: 'rgba(8,17,32,.5)', borderRadius: '0 0 16px 16px', flex: '0 0 auto' }}>
            <Button
              type="submit"
              form="quick-evaluation-form"
              variant="contained"
              disabled={isOptimizeButtonDisabled}
              startIcon={uiState.name === 'optimizing' ? <CircularProgress size={18} color="inherit" /> : undefined}
              sx={{
                minHeight: 48, px: '22px', borderRadius: '10px', fontSize: 15, fontWeight: 700, textTransform: 'none', boxShadow: 'none',
                bgcolor: '#38bdf8', color: '#0f172a',
                '&:hover': { bgcolor: '#7dd3fc', boxShadow: 'none' },
                '&.Mui-disabled': { bgcolor: '#334155', color: '#94a3b8' },
              }}
            >
              {uiState.name === 'optimizing' ? 'Calculando…' : 'Calcular precio recomendado'}
            </Button>
          </Box>
        </Box>

        {/* PRC-038: columna derecha: lo que responde PROA, a la vista a la vez que el formulario. */}
        <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Antes de calcular: explica qué aparecerá. */}
      {uiState.name === 'idle' && systemState === 'ACTIVE' && (
        <Box sx={{ border: '1px dashed #334155', borderRadius: '16px', p: { xs: 2.5, md: 3.5 }, color: '#94a3b8' }}>
          <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 800, mb: 1 }}>Aquí verás la respuesta de PROA</Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            Elige cliente, producto y estrategia y pulsa «Calcular precio recomendado». Aparecerán el precio que recomienda PROA,
            el margen sobre ingresos (en % y en € por unidad), el precio de tarifa y el descuento, el puente de valor con las
            referencias del modelo y los precios por volumen.
          </Typography>
        </Box>
      )}
      {/* PRC-038/041: el motor se entrena solo tras la primera carga; aquí solo se espera. */}
      {systemState === 'NEEDS_TRAINING' && (
        <StatusBlock
          variant="loading"
          chip={<JourneyChip tone="info">Suele tardar de 30 min a 3 h según el volumen (unos 30 min con 50.000 ventas)</JourneyChip>}
          title="Preparando el motor de precios"
          text="Puedes rellenar el formulario; el botón se activará cuando el motor esté listo. Te avisaremos por email."
        />
      )}
      {systemState === 'ENGINE_FAILED' && <EnginePanel compact />}
      {/* ================================================================== */}
      {/* === VISUALIZACIÓN DE ESTADOS (LOADING / ERROR / SUCCESS)       === */}
      {/* ================================================================== */}

      {/* PRC-037: mientras calcula, el botón dice «Calculando…» y aquí se ve la forma del resultado. */}
      {uiState.name === 'optimizing' && (
        <Box aria-busy="true" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <SkeletonBlock h={150} radius="16px" />
            <SkeletonBlock h={260} radius="16px" />
        </Box>
      )}

      {uiState.name === 'error' && (
        <Alert 
            severity="error" 
            variant="filled" 
            sx={{ 
                my: 3, 
                bgcolor: 'rgba(239, 68, 68, 0.1)', 
                border: '1px solid #ef4444', 
                color: '#fca5a5' 
            }}
        >
          <AlertTitle sx={{ fontWeight: 800 }}>No se ha podido calcular el precio</AlertTitle>
          {getOptimizeErrorMessage(uiState.error)}
        </Alert>
      )}

      {uiState.name === 'success' && finalData && (() => {
        const run = lastRunRef.current;
        const strategyLabel = STRATEGY_LABEL[finalData.applied_strategy_profile || run?.strategy || ''] ?? 'manual';
        const unitMargin = finalData.precio_sugerido != null && finalData.coste_utilizado != null && finalData.coste_utilizado > 0
          ? finalData.precio_sugerido - finalData.coste_utilizado
          : null;
        const listPrice = finalData.list_price_used && finalData.list_price_used > 0 ? finalData.list_price_used : null;
        const discount = listPrice && finalData.precio_sugerido && listPrice > finalData.precio_sugerido
          ? (1 - finalData.precio_sugerido / listPrice) * 100
          : null;
        const hasMatrix = !!finalData.pricing_matrix && finalData.pricing_matrix.length > 0;
        const heroKSx = { fontSize: 11, fontWeight: 800, letterSpacing: '.12em', textTransform: 'uppercase', color: '#93abc2' };
        const heroVSx = { fontSize: 24, fontWeight: 800, color: '#f8fafc', fontVariantNumeric: 'tabular-nums' };
        const smallSx = { fontSize: 13, color: '#94a3b8', lineHeight: 1.5 };
        const cellSx = { p: '18px 20px', display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0, borderRight: { md: '1px solid #1e293b' }, borderBottom: { xs: '1px solid #1e293b', md: 'none' } };
        return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* PRC-046 (G6): franja con precio, margen, tarifa y enlace a precios por volumen. */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.3fr 1fr 1fr 1fr' }, border: '1px solid #286779', borderRadius: '14px', bgcolor: 'rgba(56,189,248,.04)' }}>
                <Box sx={cellSx}>
                    <Box component="span" sx={heroKSx}>Precio que recomienda PROA</Box>
                    <Box component="span" sx={{ ...heroVSx, fontSize: 36, color: '#a7f5e5', letterSpacing: '-.03em' }}>{formatEuro(finalData.precio_sugerido)}</Box>
                    <Box component="span" sx={smallSx}>
                        Para {run?.quantity ? `${formatInteger(run.quantity)} unidades` : '1 unidad'} · estrategia {strategyLabel} al {run?.sensitivity ?? strategySensitivity} %
                    </Box>
                </Box>
                <Box sx={cellSx}>
                    <Box component="span" sx={heroKSx}>Margen sobre ingresos</Box>
                    <Box component="span" sx={heroVSx}>{formatPercent(finalData.margen_sugerido)}</Box>
                    {/* PRC-038: regla «Margen / Margen %»: el margen en € por unidad, con el mismo coste del cálculo. */}
                    <Box component="span" sx={smallSx}>{formatEuro(unitMargin)} por unidad</Box>
                </Box>
                <Box sx={cellSx}>
                    <Box component="span" sx={heroKSx}>Precio de tarifa</Box>
                    <Box component="span" sx={heroVSx}>{formatEuro(listPrice)}</Box>
                    <Box component="span" sx={smallSx}>{discount != null ? `Descuento del ${formatPercent(discount)}` : listPrice ? 'Sin descuento' : 'Sin tarifa registrada'}</Box>
                </Box>
                <Box sx={{ ...cellSx, borderRight: 'none', borderBottom: 'none', justifyContent: 'space-between' }}>
                    <Box component="span" sx={heroKSx}>Detalle</Box>
                    {hasMatrix ? (
                        <ButtonBase
                            onClick={() => document.getElementById('pricing-matrix-section')?.scrollIntoView({ behavior: 'smooth' })}
                            sx={{ alignSelf: 'flex-start', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, color: '#7dd3fc', '&:hover': { color: '#bae6fd' } }}
                        >
                            Precios por volumen ↓
                        </ButtonBase>
                    ) : <Box component="span" sx={smallSx}>Sin precios por volumen</Box>}
                </Box>
            </Box>

            {/* Puente de valor */}
            {finalData.optimization_level_applied === 'MP' && finalData.base_historical_solid_cp != null && (
                <ValueBridge
                    base_historical_solid_cp={finalData.base_historical_solid_cp}
                    calculated_suggested_margin={finalData.margen_sugerido ? finalData.margen_sugerido / 100 : 0}
                    mei_markup_mp_plus_mde={finalData.mei_markup_mp_plus_mde || 0}
                    applied_strategy_profile={finalData.applied_strategy_profile || 'equilibrado'}
                    tacticalIntelligence={finalData.tactical_intelligence}
                />
            )}

            {/* Precios por volumen */}
            {hasMatrix && (
                <Box component="section" id="pricing-matrix-section" sx={{ ...panelSx, scrollMarginTop: 16 }}>
                    <Box sx={{ p: '20px 24px', borderBottom: '1px solid #1e293b' }}>
                        <Box component="h2" sx={{ fontSize: 16, fontWeight: 700, m: 0, color: '#f8fafc' }}>Precios por volumen</Box>
                        <Box component="p" sx={{ fontSize: 13, color: '#94a3b8', m: '4px 0 0' }}>
                            Rangos calculados con tu histórico: cantidades pequeñas, habituales y grandes de este producto.
                        </Box>
                    </Box>
                    <Box sx={{ p: '6px 24px 20px', display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <TableContainer>
                            <Table size="small" sx={{
                                '& .MuiTableCell-root': { borderColor: '#16233a', color: '#e2e8f0', fontSize: 13, p: '12px', fontVariantNumeric: 'tabular-nums' },
                                '& .MuiTableCell-head': { color: '#94a3b8', fontWeight: 700, fontSize: 11, letterSpacing: '.12em', textTransform: 'uppercase', borderColor: '#1e293b', p: '10px 12px' },
                            }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <Stack direction="row" alignItems="center" spacing={0.75}>
                                                <span>Cantidad</span>
                                                <Tooltip title="Estos rangos se calculan automáticamente con el historial de transacciones (cantidades pequeñas, habituales y grandes de este producto)." arrow placement="top">
                                                    <InfoOutlinedIcon sx={{ fontSize: 15, color: '#64748b', cursor: 'help' }} />
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="right">Precio recomendado</TableCell>
                                        <TableCell align="right">Descuento</TableCell>
                                        <TableCell align="right">Margen por unidad</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {finalData.pricing_matrix!.map((tier, index) => {
                                        const tierName = index === 0 ? 'Puntual' : index === 1 ? 'Recurrente' : index === 2 ? 'Gran escala' : 'Volumen';
                                        const tierColor = index === 1 ? '#38bdf8' : index === 2 ? '#4ade80' : '#94a3b8';
                                        return (
                                            <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                <TableCell component="th" scope="row">
                                                    <Box component="b" sx={{ color: tierColor, display: 'block' }}>{tierName}</Box>
                                                    <Box component="span" sx={{ fontSize: 13, color: '#94a3b8' }}>{formatInteger(tier.quantity)} uds</Box>
                                                </TableCell>
                                                <TableCell align="right"><b>{formatEuro(tier.suggested_price)}</b></TableCell>
                                                <TableCell align="right">
                                                    {tier.suggested_discount > 0 ? (
                                                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', height: 22, px: 1, borderRadius: '6px', fontSize: 11, fontWeight: 800, letterSpacing: '.06em', bgcolor: 'rgba(74,222,128,.12)', color: '#86efac' }}>
                                                            {formatPercent(-tier.suggested_discount * 100)}
                                                        </Box>
                                                    ) : '—'}
                                                </TableCell>
                                                <TableCell align="right">{formatEuro(tier.unit_margin)}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {listPrice != null && (
                            <Box component="span" sx={{ fontSize: 13, color: '#94a3b8', textAlign: 'right' }}>
                                * Descuentos calculados sobre el precio de tarifa: {formatEuro(listPrice)}
                            </Box>
                        )}
                    </Box>
                </Box>
            )}
        </Box>
        );
      })()}
        </Box>
      </Box>
    </Box>
  );
};

export default OptimizationPage;